<div class="settings-section">
  <h3 class="section-heading">Azure Tenant Configuration</h3>
  <p class="section-desc">
    Assign a service principal credential set to this class. Credentials are managed centrally
    as <strong>Tenant Secrets</strong> and can be shared across classes of the same customer.
  </p>

  <!-- Current assignment banner -->
  <div *ngIf="assignedSecret" class="assigned-banner">
    <mat-icon class="assigned-icon">verified_user</mat-icon>
    <div class="assigned-text">
      <strong>{{ assignedSecret.label }}</strong>
      <span>Tenant Name: {{ assignedSecret.tenantName }} &nbsp;|&nbsp; Id: {{ assignedSecret.tenantId }}</span>
      <span>Client Name: {{ assignedSecret.clientName }} &nbsp;|&nbsp; Id: {{ assignedSecret.clientId ??
        'Fetching...' }}</span>
    </div>
  </div>

  <div *ngIf="!assignedSecret && !isLoadingSecrets" class="unassigned-banner">
    <mat-icon class="unassigned-icon">warning</mat-icon>
    <span>No tenant credential is assigned to this class. Provisioning will fail until one is set.</span>
  </div>

  <div *ngIf="isTenantLocked" class="tenant-locked-banner">
    <mat-icon class="locked-icon">lock</mat-icon>
    <span>Tenant credential is locked because this class has active subscriptions. Remove all
      VM and Account subscriptions to change the tenant.</span>
  </div>
    
  <!-- Cascading Dropdowns -->
  <div class="dropdown-stack">
    
    <!-- DROPDOWN 1: Select Tenant -->
    <mat-form-field appearance="outline" class="form-field-full">
      <mat-label>Select Tenant</mat-label>
      <mat-select [(ngModel)]="selectedTenantId" (selectionChange)="onTenantChange($event.value)"
        [disabled]="isLoadingSecrets || isAssigning || isTenantLocked"
        [matTooltip]="isTenantLocked ? 'Cannot change Tenant while subscriptions exist.' : ''" matTooltipPosition="above">
        <mat-option *ngIf="isLoadingSecrets" disabled>Loading…</mat-option>
        <mat-option *ngFor="let tenant of tenantGroups" [value]="tenant.tenantId">
          {{ getTenantDisplayLabel(tenant) }}
        </mat-option>
      </mat-select>
      <mat-hint *ngIf="tenantGroups.length === 0 && !isLoadingSecrets">
        No tenants available — create one below.
      </mat-hint>
    </mat-form-field>

    <!-- DROPDOWN 2: Select Client / Credential (Disabled until Dropdown 1 selected) -->
    <mat-form-field appearance="outline" class="form-field-full">
      <mat-label>Select Client</mat-label>
      <mat-select [(ngModel)]="selectedSecretId"
        [disabled]="!selectedTenantId || isLoadingSecrets || isAssigning || isTenantLocked"
        [matTooltip]="!selectedTenantId ? 'Please select a Tenant first' : ''" matTooltipPosition="above">
        <mat-option *ngFor="let secret of clientsForSelectedTenant" [value]="secret.id">
          {{ getClientDisplayLabel(secret) }}
        </mat-option>
      </mat-select>
      <mat-hint *ngIf="selectedTenantId && clientsForSelectedTenant.length === 0">
        No clients under this tenant.
      </mat-hint>
    </mat-form-field>

  </div>

  <!-- Action Buttons Row (Assign & Create Side-by-Side) -->
  <div class="action-btn-row">
    <button mat-flat-button color="primary" class="assign-btn"
      [disabled]="!selectedSecretId || selectedSecretId === assignedSecretId || isAssigning || isTenantLocked"
      [matTooltip]="isTenantLocked ? 'Locked while this class has active subscriptions.' : ''" 
      (click)="assign()">
      <mat-spinner *ngIf="isAssigning" diameter="18" class="btn-spinner"></mat-spinner>
      <mat-icon *ngIf="!isAssigning">link</mat-icon>
      {{ isAssigning ? 'Assigning…' : 'Assign' }}
    </button>

    <button mat-stroked-button type="button" class="create-btn" (click)="toggleCreatePanel()" [disabled]="isTenantLocked"
      [matTooltip]="isTenantLocked ? 'Locked while this class has active subscriptions.' : ''">
      <mat-icon>{{ showCreatePanel ? 'expand_less' : 'add' }}</mat-icon>
      {{ showCreatePanel ? 'Cancel' : 'Create New Credential' }}
    </button>
  </div>

  <!-- Inline Create Panel -->

  <div *ngIf="showCreatePanel" class="create-panel" [formGroup]="createForm">
    <h4 class="create-heading">New Tenant Credential</h4>

    <mat-form-field appearance="outline" class="form-field-full">
      <mat-label>Label</mat-label>
      <input matInput formControlName="label" placeholder="e.g. Contoso Azure Prod SP">
      <mat-hint>Unique per customer — used to identify the credential in the dropdown.</mat-hint>
      <mat-error *ngIf="createForm.get('label')?.hasError('required')">Label is required</mat-error>
      <mat-error *ngIf="createForm.get('label')?.hasError('maxlength')">Maximum 200 characters</mat-error>
    </mat-form-field>

    <mat-form-field appearance="outline" class="form-field-full">
      <mat-label>Tenant ID</mat-label>
      <input matInput formControlName="tenantId" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx">
      <mat-icon matSuffix matTooltip="Azure Active Directory Tenant ID">domain</mat-icon>
      <mat-error *ngIf="createForm.get('tenantId')?.hasError('required')">Tenant ID is required</mat-error>
    </mat-form-field>

    <mat-form-field appearance="outline" class="form-field-full">
      <mat-label>Client ID (Application ID)</mat-label>
      <input matInput formControlName="clientId" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx">
      <mat-icon matSuffix matTooltip="Azure App Registration Client ID">fingerprint</mat-icon>
      <mat-error *ngIf="createForm.get('clientId')?.hasError('required')">Client ID is required</mat-error>
    </mat-form-field>

    <mat-form-field appearance="outline" class="form-field-full">
      <mat-label>Client Secret</mat-label>
      <input matInput formControlName="clientSecret" [type]="showCreateSecret ? 'text' : 'password'"
        placeholder="Enter client secret…">
      <button mat-icon-button matSuffix type="button" [matTooltip]="showCreateSecret ? 'Hide secret' : 'Show secret'"
        (click)="showCreateSecret = !showCreateSecret">
        <mat-icon>{{ showCreateSecret ? 'visibility_off' : 'visibility' }}</mat-icon>
      </button>
      <mat-error *ngIf="createForm.get('clientSecret')?.hasError('required')">Client secret is required</mat-error>
    </mat-form-field>

    <div class="create-actions">
      <button mat-flat-button color="accent" [disabled]="createForm.invalid || isCreating" (click)="createAndAssign()">
        <mat-spinner *ngIf="isCreating" diameter="18" class="btn-spinner"></mat-spinner>
        <mat-icon *ngIf="!isCreating">save</mat-icon>
        {{ isCreating ? 'Saving…' : 'Create & Assign' }}
      </button>
    </div>
  </div>
</div>